import type { Difficulty, FillQuestion } from './types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

/**
 * N-3-5：三位數 ÷ 一位數直式計算（有餘或整除）。
 * difficulty 1：100-299 ÷ 2-4
 * difficulty 2：200-599 ÷ 3-7
 * difficulty 3：400-999 ÷ 4-9
 */
export function generateDiv3x1(seed: number, difficulty: Difficulty): FillQuestion {
  const rand = mulberry32(seed);

  let divisor: number;
  let dividend: number;

  if (difficulty === 1) {
    divisor = randInt(rand, 2, 4);
    dividend = randInt(rand, 100, 299);
  } else if (difficulty === 2) {
    divisor = randInt(rand, 3, 7);
    dividend = randInt(rand, 200, 599);
  } else {
    divisor = randInt(rand, 4, 9);
    dividend = randInt(rand, 400, 999);
  }

  const quotient = Math.floor(dividend / divisor);
  const remainder = dividend % divisor;

  let correctAnswer: string;
  let acceptedAnswers: string[];
  let promptSuffix: string;
  let explanation: string;

  if (remainder === 0) {
    correctAnswer = String(quotient);
    acceptedAnswers = [correctAnswer];
    promptSuffix = '= ?';
    explanation = `${divisor} × ${quotient} = ${dividend}，所以 ${dividend} ÷ ${divisor} = ${quotient}`;
  } else {
    correctAnswer = `${quotient} 餘 ${remainder}`;
    acceptedAnswers = [
      correctAnswer,
      `${quotient}餘${remainder}`,
      `${quotient}r${remainder}`,
      `${quotient} r ${remainder}`,
      `${quotient}R${remainder}`,
    ];
    promptSuffix = '= ? 餘 ?';
    explanation = `${divisor} × ${quotient} = ${divisor * quotient}，${dividend} − ${divisor * quotient} = ${remainder} → 商 ${quotient} 餘 ${remainder}`;
  }

  return {
    kind: 'fill',
    prompt: `${dividend} ÷ ${divisor} ${promptSuffix}`,
    correctAnswer,
    acceptedAnswers,
    explanation,
    kp: 'math.div.mul-relation',
  };
}
